/**
 * The Reporting page: one library of reports, the schedules behind them, and the
 * delivery record, with what needs a person at the top.
 *
 * What this replaced, and why:
 *
 *   A table of every saved report, every generated file and every schedule in
 *   one list: a report that ran forty times was forty rows. A Subscriptions tab
 *   whose "Next run" read "43d ago" for schedules that were drafts and never
 *   ran. A delivery history that printed "Recipient not recorded" for a report
 *   that was kept and never emailed. Nothing said whether the deployment would
 *   send mail at all.
 *
 * Now each report is listed once with its files as runs; a schedule shows a
 * next run only when it really will run; a delivery says who, what and why; and
 * the first thing on the page is whatever needs attention. The design is the
 * "Helios Reporting Redesign" canvas, built on the endpoints that already
 * existed plus a few additive fields (see backend/app/api/v1/reports.py).
 *
 * Pipeline reports are still designed in Report Studio; every other report is
 * edited in the field editor. Both open from here.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CheckCircle, Plus, Search, X, AlertTriangle, ChevronLeft } from 'lucide-react'
import clsx from 'clsx'
import DialogShell from '@/components/crm/DialogShell'
import type { ReportDefinition } from '@/services/reportDefinitions'
import type { ReportSchedule } from './../pipeline-intelligence/types'
import { ReportEditor } from './ReportEditor'
import { ReportingSettings } from './ReportingSettings'
import { PipelineReportStudio } from './PipelineReportStudio'
import { DeliveriesTab } from './center/DeliveriesTab'
import type { DeliveryFilter } from './center/deliveries'
import { favoriteIdToStore, favoriteIdsOf, readFavorites, writeFavorites } from './center/favorites'
import { LibraryTab } from './center/LibraryTab'
import type { CollectionId, LibraryReport, LibrarySort } from './center/library'
import { NewReportDialog } from './center/NewReportDialog'
import type { ReportHandlers } from './center/ReportPanel'
import { ReportDetailPage } from './center/ReportDetailPage'
import { ScheduleDialog, type ScheduleTarget } from './center/ScheduleDialog'
import { SchedulesTab } from './center/SchedulesTab'
import { ShareDialog } from './center/ShareDialog'
import { REPORTING_TABS, resolveTab, tabParam, type ReportingTab } from './center/tabs'
import { Btn, EmptyState, FOCUS, SkeletonRows, TabList } from './center/ui'
import { useReportActions, type Notice } from './center/useReportActions'
import { useReportPermissions, useReportingData } from './center/useReportingData'

/** Wide enough for the three-column library: collections, reports, and the selected report. */
const DOCKED_QUERY = '(min-width: 1400px)'

function useDocked(): boolean {
  const read = () => (typeof window.matchMedia === 'function' ? window.matchMedia(DOCKED_QUERY).matches : true)
  const [docked, setDocked] = useState(read)
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const query = window.matchMedia(DOCKED_QUERY)
    const onChange = () => setDocked(query.matches)
    onChange()
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])
  return docked
}

/** How long a confirmation stays on screen. */
const NOTICE_SECONDS = 12

const SEARCH_LABELS: Record<ReportingTab, string> = {
  library: 'Search reports, owners, deals',
  schedules: 'Search schedules, recipients',
  deliveries: 'Search deliveries, recipients',
  branding: '',
}

export default function ReportingCommandCenter() {
  const [searchParams, setSearchParams] = useSearchParams()
  const perms = useReportPermissions()
  const data = useReportingData()
  const docked = useDocked()

  const requested = resolveTab(searchParams.get('tab'))
  const tab = requested.tab
  const reportKey = searchParams.get('report')

  const [query, setQuery] = useState('')
  const [collection, setCollection] = useState<CollectionId>('all')
  const [sort, setSort] = useState<LibrarySort>('recent')
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [deliveryFilter, setDeliveryFilter] = useState<DeliveryFilter>('all')
  const [notice, setNotice] = useState<Notice | null>(null)
  const [favorites, setFavorites] = useState<string[]>(readFavorites)
  const favoriteSet = useMemo(() => new Set(favorites), [favorites])

  const [editingId, setEditingId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  // Report Studio opens over the library, the way the field editor does.
  const [studioOpen, setStudioOpen] = useState(requested.openStudio)
  // A pipeline report is a saved view, so its id is the view's. Null opens the workspace's
  // first report, which is what the bare `?tab=pipeline` link means.
  const [studioViewId, setStudioViewId] = useState<string | null>(null)
  const [scheduling, setScheduling] = useState<ScheduleTarget | null>(null)
  const [sharing, setSharing] = useState<ReportDefinition | null>(null)

  const actions = useReportActions({ deals: data.deals, me: data.me, setNotice })

  // A confirmation has done its job after a while; a refusal stays until it is read and dismissed.
  useEffect(() => {
    if (!notice || notice.tone !== 'ok') return
    const timer = window.setTimeout(() => setNotice(null), NOTICE_SECONDS * 1000)
    return () => window.clearTimeout(timer)
  }, [notice])

  const editing = data.definitions.find((definition) => definition.id === editingId) ?? null
  const detail = reportKey ? (data.library.find((report) => report.key === reportKey) ?? null) : null

  const goTab = useCallback(
    (next: ReportingTab, options?: { deliveryFilter?: 'failed' }) => {
      if (options?.deliveryFilter) setDeliveryFilter(options.deliveryFilter)
      const param = tabParam(next)
      setSearchParams(param ? { tab: param } : {}, { replace: true })
    },
    [setSearchParams],
  )

  const openDetail = useCallback(
    (report: LibraryReport) => {
      const next: Record<string, string> = { report: report.key }
      const param = tabParam(tab)
      if (param) next.tab = param
      setSearchParams(next)
    },
    [setSearchParams, tab],
  )

  const closeDetail = useCallback(() => {
    const param = tabParam(tab)
    setSearchParams(param ? { tab: param } : {})
  }, [setSearchParams, tab])

  /** Where a report is changed. A pipeline report's columns, filters, grouping, totals, style and paper
   *  live in Report Studio; the field editor has none of them. */
  const openDefinition = useCallback((definition: ReportDefinition) => {
    if (definition.reportType === 'pipeline') {
      setStudioViewId(definition.id)
      setStudioOpen(true)
      return
    }
    setEditingId(definition.id)
  }, [])

  const toggleFavorite = useCallback((report: LibraryReport) => {
    setFavorites((current) => {
      const starred = favoriteIdsOf(report).some((id) => current.includes(id))
      // Un-starring removes every id the report was ever starred under; starring stores one.
      const next = starred
        ? current.filter((id) => !favoriteIdsOf(report).includes(id))
        : [...current, favoriteIdToStore(report)]
      writeFavorites(next)
      return next
    })
  }, [])

  const handlers = useMemo<ReportHandlers>(
    () => ({
      open: (report) => {
        if (report.definition) {
          openDefinition(report.definition)
          return
        }
        if (['pipeline_summary', 'pipeline_backshop', 'current_status', 'pipeline_daily'].includes(report.template)) {
          // Columns, grouping, totals and the print layout live in the Studio, not in the field
          // editor — so a pipeline report opens there. Current Status has its own starting view;
          // the others open the active pipeline.
          setStudioViewId(report.template === 'current_status' ? 'current-status' : null)
          setStudioOpen(true)
          return
        }
        // A generated file or a schedule is a record of a run, not a design. The honest move is to
        // offer a saved report of the same kind rather than pretend the record itself is editable.
        setCreating(true)
      },
      schedule: (report) => setScheduling({ mode: 'new', report }),
      editSchedule: (schedule: ReportSchedule) => setScheduling({ mode: 'edit', schedule }),
      share: (report) => report.definition && setSharing(report.definition),
      detail: openDetail,
    }),
    [openDefinition, openDetail],
  )

  const showSearch = tab !== 'branding' && !detail

  return (
    <>
      {editing ? (
        <DialogShell
          label={`Edit ${editing.name}`}
          onClose={() => setEditingId(null)}
          width="landscape"
          testId="report-editor-modal"
          header={<div className="sr-only" />}
        >
          <div className="reporting-center flex h-full min-h-0 flex-col bg-[#0a0a0a] p-3 text-stone-100">
            <ReportEditor
              definition={editing}
              deals={data.deals}
              onBack={() => setEditingId(null)}
              onSaved={() => void actions.invalidate()}
            />
          </div>
        </DialogShell>
      ) : null}

      <div
        className="reporting-center flex h-full min-h-0 flex-col bg-[#060606] text-[13px] leading-[1.45] text-[#ECEAE4]"
        data-testid="reporting-command-center"
      >
        {/* One header. In the list it is the page title, the search and the way to make a report;
            on a report's own page it is the way back. */}
        {detail || reportKey ? (
          <header className="flex shrink-0 flex-wrap items-center gap-3 border-b border-[#1E1E1E] bg-[#0A0A0A] px-6 py-3">
            <button
              type="button"
              onClick={closeDetail}
              className={clsx('inline-flex items-center gap-1.5 rounded text-[13px] text-[#C9C9C9] hover:text-white', FOCUS)}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
              Command Center
            </button>
            {detail ? (
              <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-2 text-[13px]">
                <span aria-hidden className="text-[#3A3A3A]">/</span>
                <span className="text-[#8F8F8F]">{detail.bucket.name}</span>
                <span aria-hidden className="text-[#3A3A3A]">/</span>
                <span className="truncate text-[#ECEAE4]" aria-current="page">{detail.name}</span>
              </nav>
            ) : null}
          </header>
        ) : (
          <header className="flex shrink-0 flex-wrap items-center gap-x-5 gap-y-3 border-b border-[#1E1E1E] bg-[#0A0A0A] px-6 py-4">
            <div className="flex min-w-0 flex-[1_1_420px] flex-wrap items-baseline gap-x-4 gap-y-1">
              <h1 className="font-display text-[32px] font-semibold leading-none text-[#F5F3EE]">Reporting Command Center</h1>
              <p className="text-[13px] text-[#8F8F8F]">Build, run, schedule and deliver every report the desk produces.</p>
            </div>
            {showSearch ? (
              <label className="relative flex h-9 min-w-[220px] max-w-[460px] flex-[1_1_300px] items-center">
                <Search className="pointer-events-none absolute left-3 h-4 w-4 text-[#8F8F8F]" aria-hidden />
                <span className="sr-only">{SEARCH_LABELS[tab]}</span>
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={SEARCH_LABELS[tab]}
                  className={clsx(
                    'h-9 w-full rounded-lg border border-[#262626] bg-[#101010] pl-9 pr-3 text-[13px] text-[#ECEAE4] placeholder:text-[#808080] focus:border-[#FFD86B] focus:outline-none',
                  )}
                />
              </label>
            ) : null}
            <Btn
              variant="primary"
              icon={<Plus className="h-4 w-4" aria-hidden />}
              disabled={!perms.canEdit}
              title={perms.canEdit ? undefined : 'Your role can view and run reports but not create them.'}
              onClick={() => setCreating(true)}
              className="ml-auto"
            >
              New report
            </Btn>
          </header>
        )}

        {notice ? (
          <div
            role={notice.tone === 'bad' ? 'alert' : 'status'}
            className={clsx(
              'mx-6 mt-3 flex shrink-0 items-start gap-2 rounded-lg border px-3 py-2 text-[12.5px]',
              notice.tone === 'bad'
                ? 'border-[#4A3220] bg-[#140E0A] text-[#F0B08A]'
                : 'border-[#4A3F1E] bg-[#12100A] text-[#E8D9A8]',
            )}
          >
            {notice.tone === 'bad' ? (
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            ) : (
              <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#FFD86B]" aria-hidden />
            )}
            <span className="min-w-0 flex-1">{notice.text}</span>
            <button
              type="button"
              onClick={() => setNotice(null)}
              aria-label="Dismiss notification"
              className={clsx('rounded text-[#A2A2A2] hover:text-white', FOCUS)}
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        ) : null}

        {reportKey ? (
          detail ? (
            <ReportDetailPage
              report={detail}
              data={data}
              actions={actions}
              perms={perms}
              handlers={handlers}
              deliveryFilter={deliveryFilter}
              onDeliveryFilter={setDeliveryFilter}
              onDeleted={closeDetail}
            />
          ) : data.loading ? (
            <div className="px-6 py-6">
              <SkeletonRows label="Loading the report" count={2} />
            </div>
          ) : (
            <div className="px-6 py-8">
              <EmptyState
                title="This report is no longer in the library"
                body="It may have been deleted, or the link is from another workspace."
                action={
                  <Btn variant="secondary" onClick={closeDetail}>
                    Back to the Command Center
                  </Btn>
                }
              />
            </div>
          )
        ) : (
          <>
            <div className="shrink-0 border-b border-[#1E1E1E] bg-[#0A0A0A] px-6 pb-2.5">
              <TabList
                label="Reporting views"
                tabs={REPORTING_TABS}
                active={tab}
                onChange={(next) => goTab(next)}
                idPrefix="reporting"
              />
            </div>
            <div
              role="tabpanel"
              id={`reporting-panel-${tab}`}
              aria-labelledby={`reporting-tab-${tab}`}
              className="flex min-h-0 flex-1 flex-col"
            >
              {tab === 'library' ? (
                <LibraryTab
                  data={data}
                  actions={actions}
                  perms={perms}
                  handlers={handlers}
                  query={query}
                  collection={collection}
                  onCollection={setCollection}
                  sort={sort}
                  onSort={setSort}
                  selectedKey={selectedKey}
                  onSelect={setSelectedKey}
                  favorites={favoriteSet}
                  onToggleFavorite={toggleFavorite}
                  docked={docked}
                  onGoTab={goTab}
                />
              ) : null}
              {tab === 'schedules' ? (
                <SchedulesTab
                  data={data}
                  actions={actions}
                  perms={perms}
                  query={query}
                  onOpenReport={openDetail}
                  onEditSchedule={handlers.editSchedule}
                />
              ) : null}
              {tab === 'deliveries' ? (
                <DeliveriesTab
                  data={data}
                  actions={actions}
                  perms={perms}
                  query={query}
                  filter={deliveryFilter}
                  onFilter={setDeliveryFilter}
                />
              ) : null}
              {tab === 'branding' ? (
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-4">
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-[#222] bg-[#0d0d0d]">
                    <ReportingSettings deals={data.deals} />
                  </div>
                </div>
              ) : null}
            </div>
          </>
        )}

        {studioOpen ? (
          <PipelineReportStudio
            initialViewId={studioViewId}
            onClose={() => {
              setStudioOpen(false)
              setStudioViewId(null)
              // The Studio saves versions, names and sharing; the library shows them.
              void actions.invalidate()
            }}
          />
        ) : null}

        {creating ? (
          <NewReportDialog
            existing={data.definitions}
            onClose={() => setCreating(false)}
            onOpenExisting={(definition) => {
              setCreating(false)
              openDefinition(definition)
            }}
            onCreated={async (definition) => {
              setCreating(false)
              await actions.invalidate()
              openDefinition(definition)
            }}
          />
        ) : null}

        {scheduling ? (
          <ScheduleDialog
            target={scheduling}
            senderEmail={data.senderEmail}
            onClose={() => setScheduling(null)}
            onOpenStudio={
              scheduling.mode === 'new' && scheduling.report.definition?.reportType === 'pipeline'
                ? () => {
                    const definition = scheduling.report.definition as ReportDefinition
                    setScheduling(null)
                    openDefinition(definition)
                  }
                : undefined
            }
            onSaved={async (message) => {
              setScheduling(null)
              setNotice({ tone: 'ok', text: message })
              await actions.invalidate()
            }}
          />
        ) : null}

        {sharing ? (
          <ShareDialog
            definition={sharing}
            saving={actions.share.isPending}
            onClose={() => setSharing(null)}
            onSave={(isShared) =>
              actions.share.mutate(
                { id: sharing.id, isShared },
                { onSuccess: () => setSharing(null) },
              )
            }
          />
        ) : null}
      </div>
    </>
  )
}
